import React from 'react';
import './Icon.css';

/*
 * Icon — inline-SVG icon set. No icon library, no external requests.
 * Every glyph is a 24x24 viewBox path set; strokes use currentColor so an
 * icon inherits the text color of its context. Sizing is via the `size` prop
 * (px) or CSS (.ui-icon { width/height }).
 *
 * Names match what the "Console" mock draws inline, plus the full set the
 * reskin needs. Unknown names render nothing (null) rather than throwing.
 */

// Stroked glyphs: rendered inside a shared <svg stroke=currentColor> wrapper.
// Value is the inner markup (paths/shapes) as a React fragment.
const STROKE = {
  dashboard: (
    <><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M3 9h18M8 21h8" /></>
  ),
  users: (
    <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c.6-3 3-4.6 5.5-4.6s4.9 1.6 5.5 4.6M17 11.5a2.6 2.6 0 1 0-1.5-4.7M21 19c-.4-2-1.7-3.4-3.5-4" /></>
  ),
  user: (
    <><circle cx="12" cy="8" r="3.4" /><path d="M5 20c.7-3.6 3.5-5.5 7-5.5S18.3 16.4 19 20" /></>
  ),
  reports: (
    <><path d="M4 19V5M4 19h16" /><rect x="7" y="11" width="3" height="5" /><rect x="12" y="8" width="3" height="8" /><rect x="17" y="13" width="3" height="3" /></>
  ),
  // 2x2 grid — used for the Gates nav item
  grid: (
    <><rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="6" rx="1.5" /><rect x="14" y="14" width="7" height="6" rx="1.5" /></>
  ),
  // boom-barrier gate — used for gate leaves in the Customer▸Site▸Gate tree
  gate: (
    <><path d="M5 21V6" /><path d="M2.5 21h5" /><rect x="5" y="5.4" width="15.5" height="3.6" rx="1.1" /><path d="M10 5.4v3.6M15 5.4v3.6" /></>
  ),
  assignments: (
    <><path d="M4 7h16M4 12h16M4 17h10" /><circle cx="18.5" cy="17" r="2.2" /></>
  ),
  company: (
    <><path d="M3 21V7l6-3 6 3v14M9 21v-4h0M15 11h6v10M7 8h.01M11 8h.01M7 12h.01M11 12h.01" /></>
  ),
  plate: (
    <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M7 15h5M7 10h10" /></>
  ),
  // Org / hierarchy tree — used for the Customers & Sites nav item
  tree: (
    <><path d="M12 3v6M6 21v-5a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v5" /><circle cx="12" cy="4" r="1.6" /><circle cx="5" cy="20" r="1.6" /><circle cx="19" cy="20" r="1.6" /></>
  ),
  settings: (
    <><circle cx="12" cy="12" r="3.6" /><path d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" /></>
  ),
  vehicle: (
    <><path d="M3 13l2-5a2 2 0 0 1 1.9-1.3h10.2A2 2 0 0 1 21 8l2 5v4a1 1 0 0 1-1 1h-1M3 13v4a1 1 0 0 0 1 1h1" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></>
  ),
  sun: (
    <><circle cx="12" cy="12" r="3.6" /><path d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" /></>
  ),
  moon: (
    <><path d="M20 14.5A8 8 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z" /></>
  ),
  monitor: (
    <><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></>
  ),
  search: (
    <><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></>
  ),
  close: (
    <><path d="M6 6l12 12M18 6 6 18" /></>
  ),
  chevronRight: (
    <><path d="m9 6 6 6-6 6" /></>
  ),
  chevronDown: (
    <><path d="m6 9 6 6 6-6" /></>
  ),
  chevronLeft: (
    <><path d="m15 6-6 6 6 6" /></>
  ),
  plus: (
    <><path d="M12 5v14M5 12h14" /></>
  ),
  edit: (
    <><path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z" /><path d="M13.5 6.5l3 3" /></>
  ),
  trash: (
    <><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6M14 11v6" /></>
  ),
  check: (
    <><path d="M20 6 9 17l-5-5" /></>
  ),
  warning: (
    <><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>
  ),
  camera: (
    <><rect x="3" y="6.5" width="18" height="13" rx="2" /><circle cx="12" cy="13" r="3.2" /><path d="M8.5 6.5 10 4h4l1.5 2.5" /></>
  ),
  cameraOff: (
    <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M4 5 20 19" /></>
  ),
  download: (
    <><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>
  ),
  logout: (
    <><path d="M14 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2M9 12h12m0 0-3-3m3 3-3 3" /></>
  ),
  clock: (
    <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>
  ),
  location: (
    <><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" /><circle cx="12" cy="10" r="2.4" /></>
  ),
  calendar: (
    <><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>
  ),
  mail: (
    <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>
  ),
  refresh: (
    <><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M21 4v4h-4M3 20v-4h4" /></>
  ),
  muster: (
    <><path d="M9 11l3 3 8-8M4 12v6a2 2 0 0 0 2 2h12" /></>
  ),
};

// Filled glyphs: the brand shield brandmark from the mock (uses fill, not stroke).
const FILLED = {
  shield: (
    <>
      <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" fill="var(--brand)" />
      <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11V2Z" fill="#3ea0e0" />
      <path d="M9 12.5 11 15l4-5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </>
  ),
};

// Convenience aliases so callers can use intent-named or mock-named keys.
const ALIAS = {
  eventMonitor: 'dashboard',
  onsite: 'users',
  gates: 'grid',
  customers: 'tree',
  org: 'tree',
  people: 'user',
  guards: 'user',
  x: 'close',
  chevron: 'chevronRight',
  add: 'plus',
  delete: 'trash',
  export: 'download',
  alert: 'warning',
  brand: 'shield',
};

export default function Icon({ name, size = 18, className = '', title, strokeWidth = 1.7, ...rest }) {
  const key = ALIAS[name] || name;
  const stroked = STROKE[key];
  const filled = FILLED[key];
  if (!stroked && !filled) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[ui/Icon] unknown icon name: "${name}"`);
    }
    return null;
  }

  const common = {
    className: `ui-icon${className ? ' ' + className : ''}`,
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    role: title ? 'img' : 'presentation',
    'aria-hidden': title ? undefined : true,
    'aria-label': title,
    focusable: 'false',
    ...rest,
  };

  if (filled) {
    return (
      <svg {...common} fill="none">
        {title ? <title>{title}</title> : null}
        {filled}
      </svg>
    );
  }
  return (
    <svg {...common} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {title ? <title>{title}</title> : null}
      {stroked}
    </svg>
  );
}

// Named export of available icon keys (useful for pickers / tests).
export const iconNames = [...Object.keys(STROKE), ...Object.keys(FILLED)];
